// src/components/principal/PrincipalSetupWizard.jsx (v5 – config-driven, bug-fixed)

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { API_PATHS } from "../../config";
import MobileAppOnboardingCard from "../MobileAppOnboardingCard";

/**
 * 6-step onboarding wizard for new principals.
 *
 * Guides through display name, school hashtag, mobile app install,
 * optional Instagram connect, optional staff test invite, and website prep.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.profile - Current principal profile data
 * @param {function} props.onProfileChange - Callback to update parent profile state
 * @param {function} props.onError - Error reporting callback
 * @param {number} [props.initialStep=1] - Starting step
 * @param {function} props.onComplete - Called when wizard is finished
 * @param {boolean} [props.visible=true] - Show/hide wizard
 * @returns {JSX.Element|null}
 */
const PrincipalSetupWizard = ({
  profile,
  onProfileChange,
  onError,
  initialStep = 1,
  onComplete,
  visible = true,
}) => {
  // --- Derived examples ----------------------------------------------------
  const rawPrincipalName =
    (profile.principalName ||
      profile.displayName ||
      (profile.email ? profile.email.split("@")[0] : "") ||
      "").trim();

  const principalSurname = rawPrincipalName
    ? rawPrincipalName.split(/\s+/).pop()
    : "Murphy";

  const schoolNameExample = profile.schoolName || "Your School";
  const surnameExample = principalSurname || "Murphy";

  const rawSchoolName = (profile.schoolName || "").trim();
  let schoolHashtagExample = "#YourSchoolName";
  if (rawSchoolName) {
    const camel = rawSchoolName
      .replace(/[^A-Za-z0-9\s]+/g, "")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join("");
    if (camel) schoolHashtagExample = `#${camel}`;
  }

  // --- Local wizard state --------------------------------------------------
  const [step, setStep] = useState(initialStep);

  const [displayName, setDisplayName] = useState(
    profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
  );

  const [schoolHashtag, setSchoolHashtag] = useState(
    profile.schoolHashtag ||
      (schoolHashtagExample !== "#YourSchoolName" ? schoolHashtagExample : "")
  );

  const [busy, setBusy] = useState(false);
  const [igBusy, setIgBusy] = useState(false);

  const [testStaffEmail, setTestStaffEmail] = useState("");
  const [testStaffBusy, setTestStaffBusy] = useState(false);
  const [testStaffMessage, setTestStaffMessage] = useState("");

  // Sync local state with profile changes from parent
  useEffect(() => {
    setDisplayName(
      profile.displayName || (profile.email ? profile.email.split("@")[0] : "")
    );

    // Only update local hashtag if profile has a new one (avoid overriding user input)
    setSchoolHashtag((prev) =>
      profile.schoolHashtag
        ? profile.schoolHashtag
        : prev || schoolHashtagExample
    );
  }, [
    profile.displayName,
    profile.schoolHashtag,
    profile.email,
    schoolHashtagExample,
  ]);

  // Reset step when initialStep changes (e.g., from dashboard logic)
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  if (!visible) return null;

  // --- Helpers -------------------------------------------------------------
  const normalizeHashtag = (value = "") => {
    let normalized = value.trim().replace(/\s+/g, "_");
    if (!normalized) return "";
    return normalized.startsWith("#") ? normalized : `#${normalized}`;
  };

  const advanceTo = (nextStep) => setStep(Math.max(step, nextStep));

  // Step 1: Save display name
  const handleSaveDisplayName = async (e) => {
    e?.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) {
      onError?.("Display name cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_DISPLAY_NAME, {
        new_display_name: trimmed,
      });
      onProfileChange?.({ ...profile, displayName: trimmed });
      advanceTo(2);
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to update display name.");
    } finally {
      setBusy(false);
    }
  };

  // Step 2: Save hashtag
  const handleSaveHashtag = async (e) => {
    e?.preventDefault();
    const normalized = normalizeHashtag(schoolHashtag);
    if (!normalized) {
      onError?.("School hashtag cannot be empty.");
      return;
    }

    setBusy(true);
    try {
      await api.post(API_PATHS.UPDATE_SCHOOL_HASHTAG, {
        new_hashtag: normalized,
      });
      onProfileChange?.({ ...profile, schoolHashtag: normalized });
      advanceTo(3);
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Failed to update school hashtag.");
    } finally {
      setBusy(false);
    }
  };

  // Step 3: Mark mobile app installed
  const handleAppDownloaded = async () => {
    try {
      await api.post(API_PATHS.MARK_MOBILE_INSTALLED);
      onProfileChange?.({ ...profile, hasInstalledMobileApp: true });
    } catch (err) {
      console.error("Failed to mark mobile app installed", err);
      onError?.("Couldn't record app install, but you can continue.");
    }
    advanceTo(4);
  };

  // Step 4: Instagram connect
  const handleConnectInstagram = async () => {
    setIgBusy(true);
    try {
      const { data } = await api.get(API_PATHS.INSTAGRAM_LOGIN);
      if (data.url) {
        window.location.href = data.url;
      } else {
        onError?.("Could not start Instagram connection. Try again later.");
      }
    } catch (err) {
      onError?.(
        err?.response?.data?.detail ||
          "Could not connect to Instagram. You can do this later."
      );
    } finally {
      setIgBusy(false);
    }
  };

  // Step 5: Test staff invite
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSendTestStaffInvite = async (e) => {
    e?.preventDefault();
    const email = testStaffEmail.trim();
    if (!email || !validateEmail(email)) {
      onError?.("Please enter a valid email address.");
      return;
    }

    setTestStaffBusy(true);
    try {
      const { data } = await api.post(API_PATHS.CREATE_STAFF, [{ email }]);
      setTestStaffMessage(
        data?.message || "Test invite sent successfully!"
      );
      setTestStaffEmail("");
    } catch (err) {
      onError?.(
        err?.response?.data?.detail ||
          "Failed to send test invite. You can do this later from Staff Management."
      );
    } finally {
      setTestStaffBusy(false);
    }
  };

  const handleFinish = () => onComplete?.();

  // --- Render --------------------------------------------------------------
  return (
    <div className="principal-setup-overlay">
      <div className="principal-setup-modal">
        <div className="principal-setup-header">
          <p className="principal-setup-kicker">First-time setup</p>
          <h2 className="principal-setup-title">
            {principalSurname
              ? `Welcome to GhrianGhraffy, Principal ${principalSurname}!`
              : "Welcome to GhrianGhraffy!"}
          </h2>
          <p className="principal-setup-subtitle">
            Just six quick steps (the last three are optional) and your school will be ready.
          </p>
        </div>

        <ol className="principal-setup-steps">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <li
              key={n}
              className={`principal-setup-step ${
                step === n ? "is-active" : step > n ? "is-complete" : ""
              }`}
            >
              <span className="principal-setup-step-number">{n}</span>
              <span className="principal-setup-step-text">
                {n === 1 && "Choose your display name"}
                {n === 2 && "Set your school hashtag"}
                {n === 3 && "Install the mobile app"}
                {n === 4 && "Connect Instagram (optional)"}
                {n === 5 && "Invite a staff member (optional)"}
                {n === 6 && "Get your website ready"}
              </span>
            </li>
          ))}
        </ol>

        {/* Step 1 */}
        {step === 1 && (
          <form className="principal-setup-panel" onSubmit={handleSaveDisplayName}>
            <h3>Choose how your name appears</h3>
            <p className="principal-setup-panel-text">
              Use a friendly name like <strong>“Principal {surnameExample}”</strong> or{" "}
              <strong>“{schoolNameExample} Principal”</strong>.
            </p>
            <label className="principal-setup-label">
              Display name
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={255}
                placeholder={`e.g. Principal ${surnameExample}`}
                className="principal-setup-input"
                required
              />
            </label>
            <div className="principal-setup-actions">
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <form className="principal-setup-panel" onSubmit={handleSaveHashtag}>
            <h3>Create your school hashtag</h3>
            <p className="principal-setup-panel-text">
              We use this to group your photos. Try <strong>{schoolHashtagExample}</strong>.
            </p>
            <label className="principal-setup-label">
              School hashtag
              <input
                type="text"
                value={schoolHashtag}
                onChange={(e) => setSchoolHashtag(e.target.value)}
                maxLength={50}
                placeholder={schoolHashtagExample}
                className="principal-setup-input"
                required
              />
              <span className="principal-setup-hint">
                Spaces become underscores; we’ll add the # if needed.
              </span>
            </label>
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(1)}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="submit"
                className="principal-setup-primary-btn"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="principal-setup-panel">
            <h3>Install the GhrianGhraffy app</h3>
            <p className="principal-setup-panel-text">
              This is where you and your staff will take and upload photos.
            </p>
            <MobileAppOnboardingCard
              variant="principal"
              storageKey={null}
              dismissible={false}
              onDownload={handleAppDownloaded}
            />
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(2)}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={() => advanceTo(4)}
              >
                I’ll install later
              </button>
            </div>
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="principal-setup-panel">
            <h3>Connect Instagram (optional)</h3>
            <p className="principal-setup-panel-text">
              Link your school Instagram to post approved photos with one tap.
            </p>
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(3)}
                disabled={igBusy}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={handleConnectInstagram}
                disabled={igBusy}
              >
                {igBusy ? "Connecting…" : "Connect Instagram"}
              </button>
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => advanceTo(5)}
                disabled={igBusy}
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* Step 5 */}
        {step === 5 && (
          <div className="principal-setup-panel">
            <h3>Invite a staff member to test (optional)</h3>
            <p className="principal-setup-panel-text">
              Send a test invite to one teacher to try the full workflow.
            </p>
            <form onSubmit={handleSendTestStaffInvite}>
              <input
                type="email"
                value={testStaffEmail}
                onChange={(e) => setTestStaffEmail(e.target.value)}
                placeholder="teacher@example.ie"
                className="principal-setup-input"
                style={{ maxWidth: "260px", marginRight: "0.5rem" }}
                required
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={testStaffBusy}
              >
                {testStaffBusy ? "Sending…" : "Send test invite"}
              </button>
            </form>
            {testStaffMessage && (
              <p className="principal-setup-panel-text small success">
                {testStaffMessage}
              </p>
            )}
            <div className="principal-setup-actions">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(4)}
                disabled={testStaffBusy}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={() => advanceTo(6)}
                disabled={testStaffBusy}
              >
                Next step
              </button>
            </div>
          </div>
        )}

        {/* Step 6 */}
        {step === 6 && (
          <div className="principal-setup-panel">
            <h3>Get your school website ready</h3>
            <p className="principal-setup-panel-text">
              Go to the <strong>Your School’s Website</strong> tab to get your API key and a ready-to-send email for your web admin.
            </p>
            <p className="principal-setup-panel-text small">
              They’ll use the WordPress plugin to display photos securely on your site.
            </p>
            <div className="principal-setup-actions principal-setup-actions--final">
              <button
                type="button"
                className="principal-setup-secondary-btn"
                onClick={() => setStep(5)}
              >
                Back
              </button>
              <button
                type="button"
                className="principal-setup-primary-btn"
                onClick={handleFinish}
              >
                Finish setup
              </button>
            </div>
            <p className="principal-setup-panel-text small" style={{ marginTop: "0.6rem", textAlign: "center" }}>
              This setup screen won’t appear again. You can always adjust settings from your dashboard.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalSetupWizard;